import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
  api,
  ApiError,
  type CommunityActorContextGroupRecord,
  type CommunityActorContextRecord,
  type LotStoryGroupReadPreflightBlockingReason,
  type LotStoryGroupDeliveryListRecord,
  type LotStoryGroupDeliveryRecord
} from '../utils/api'
import { useT } from '../i18n'
import LotStoryGroupDeliveryCard from './lots/LotStoryGroupDeliveryCard'
import type { LotStoryGroupDeliveryView } from './lots/lotStoryLibraryTypes'
import { getStoryFamilyLabel, getStoryFamilyTechnicalId } from './lots/lotStoryPresentation'
import { getLotStoryReadinessBadgeStyle, getLotStoryReadinessLabel } from './lots/lotStoryLibraryUi'
import { resolveStoredActorContextLabel } from './lots/storyActorLabels'

const isActiveGroupContext = (groupContext: CommunityActorContextGroupRecord): boolean => (
  groupContext.membershipStatus === 'active' && groupContext.groupStatus === 'active'
)

const formatOptionalTimestamp = (value?: string | null): string => {
  const trimmed = value?.trim()
  if (!trimmed) return ''

  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? trimmed : parsed.toLocaleString()
}

const formatProjectionCurrentSite = (delivery: LotStoryGroupDeliveryRecord | null): string => {
  const site = delivery?.version.projectionSnapshot.identityContext.currentSite
  if (!site) return '—'

  return site.displayName?.trim() || site.locationId?.trim() || '—'
}

const resolveReceiverIdentityLabel = (delivery: LotStoryGroupDeliveryRecord | null, actorContext: CommunityActorContextRecord | null): string => {
  const receiver = delivery?.deliveryContext.receiverActorContext

  return receiver?.communityDisplayName?.trim()
    || receiver?.accountDisplayName?.trim()
    || receiver?.actorId?.trim()
    || actorContext?.communityDisplayName?.trim()
    || actorContext?.accountDisplayName?.trim()
    || actorContext?.actorId?.trim()
    || ''
}

const formatGroupRoleLabel = (
  groupRole: CommunityActorContextGroupRecord['groupRole'] | undefined,
  t: ReturnType<typeof useT>
): string => groupRole ? t(`ownerConsole.groups.role.${groupRole}` as const) : ''

const isGroupDeliveryBlockingReason = (value: unknown): value is LotStoryGroupReadPreflightBlockingReason => (
  value === 'artifact-not-published'
  || value === 'artifact-not-group-visible'
  || value === 'no-active-group-context'
  || value === 'no-target-group-selected'
  || value === 'target-group-not-eligible'
  || value === 'target-group-not-saved'
)

const getGroupDeliveryBlockingReasonLabel = (
  reason: LotStoryGroupReadPreflightBlockingReason,
  t: ReturnType<typeof useT>
): string => {
  switch (reason) {
    case 'artifact-not-published':
      return t('lots.story.groupPreflight.reason.artifactNotPublished')
    case 'artifact-not-group-visible':
      return t('lots.story.groupPreflight.reason.artifactNotGroupVisible')
    case 'no-active-group-context':
      return t('lots.story.groupPreflight.reason.noActiveGroupContext')
    case 'no-target-group-selected':
      return t('lots.story.groupDelivery.reason.noTargetGroupSelected')
    case 'target-group-not-eligible':
      return t('lots.story.groupPreflight.reason.targetGroupNotEligible')
    case 'target-group-not-saved':
      return t('lots.story.groupDelivery.reason.targetGroupNotSaved')
    default:
      return reason
  }
}

const extractGroupDeliveryBlockingReasons = (error: ApiError): LotStoryGroupReadPreflightBlockingReason[] => {
  const body = error.body
  if (!body || typeof body !== 'object') {
    return []
  }

  const blockingReasons = (body as { blockingReasons?: unknown }).blockingReasons
  if (!Array.isArray(blockingReasons)) {
    return []
  }

  return blockingReasons.filter(isGroupDeliveryBlockingReason)
}

const shouldClearSelectedGroupDeliveries = (blockingReasons: LotStoryGroupReadPreflightBlockingReason[]): boolean => (
  blockingReasons.includes('no-active-group-context')
  || blockingReasons.includes('no-target-group-selected')
  || blockingReasons.includes('target-group-not-eligible')
  || blockingReasons.includes('target-group-not-saved')
)

const shouldRefreshDeliveryActorContext = (blockingReasons: LotStoryGroupReadPreflightBlockingReason[]): boolean => (
  blockingReasons.includes('no-active-group-context')
  || blockingReasons.includes('target-group-not-eligible')
)

export default function CommunityDeliveries() {
  const t = useT()
  const [actorContext, setActorContext] = useState<CommunityActorContextRecord | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedDeliveryGroupId, setSelectedDeliveryGroupId] = useState('')
  const [deliveryList, setDeliveryList] = useState<LotStoryGroupDeliveryListRecord | null>(null)
  const [deliveryListLoading, setDeliveryListLoading] = useState(false)
  const [deliveryListError, setDeliveryListError] = useState('')
  const [selectedDeliveryArtifactId, setSelectedDeliveryArtifactId] = useState('')
  const [deliveryRecord, setDeliveryRecord] = useState<LotStoryGroupDeliveryRecord | null>(null)
  const [deliveryLoading, setDeliveryLoading] = useState(false)
  const [deliveryDenied, setDeliveryDenied] = useState(false)
  const [deliveryBlockingReasonLabels, setDeliveryBlockingReasonLabels] = useState<string[]>([])
  const [deliveryError, setDeliveryError] = useState('')
  const deliveryListRequestIdRef = useRef(0)
  const deliveryDetailRequestIdRef = useRef(0)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setIsLoading(true)
      setError('')
      try {
        const resolvedActorContext = await api.communityActorContext.get()
        if (cancelled) return
        setActorContext(resolvedActorContext)
      } catch (loadError) {
        if (cancelled) return
        setError(loadError instanceof Error ? loadError.message : t('communityDeliveries.error.loadFailed'))
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [t])

  const activeGroupContexts = useMemo(() => (
    actorContext?.groupActorContexts.filter(isActiveGroupContext) ?? []
  ), [actorContext])

  useEffect(() => {
    if (activeGroupContexts.length === 0) {
      setSelectedDeliveryGroupId('')
      return
    }

    setSelectedDeliveryGroupId((previous) => (
      activeGroupContexts.some((groupContext) => groupContext.groupId === previous)
        ? previous
        : activeGroupContexts[0].groupId
    ))
  }, [activeGroupContexts])

  useEffect(() => {
    if (!selectedDeliveryGroupId.trim()) {
      deliveryListRequestIdRef.current += 1
      setDeliveryList(null)
      setDeliveryListLoading(false)
      setDeliveryListError('')
      return
    }

    const nextRequestId = deliveryListRequestIdRef.current + 1
    deliveryListRequestIdRef.current = nextRequestId
    setDeliveryListLoading(true)
    setDeliveryListError('')
    setDeliveryList(null)

    void api.lotStories.listGroupDeliveries(selectedDeliveryGroupId.trim())
      .then((result) => {
        if (deliveryListRequestIdRef.current !== nextRequestId) return
        setDeliveryList(result)
      })
      .catch((loadError) => {
        if (deliveryListRequestIdRef.current !== nextRequestId) return
        setDeliveryListError(loadError instanceof Error ? loadError.message : t('communityProfile.groupDeliveries.error.listFailed'))
      })
      .finally(() => {
        if (deliveryListRequestIdRef.current === nextRequestId) {
          setDeliveryListLoading(false)
        }
      })
  }, [selectedDeliveryGroupId, t])

  const selectedDeliveryGroupContext = useMemo(() => (
    deliveryList?.selectedGroupContext
    ?? activeGroupContexts.find((groupContext) => groupContext.groupId === selectedDeliveryGroupId)
    ?? null
  ), [activeGroupContexts, deliveryList?.selectedGroupContext, selectedDeliveryGroupId])

  const selectedDeliveryView = useMemo<LotStoryGroupDeliveryView | null>(() => {
    if (!selectedDeliveryArtifactId && !deliveryLoading && !deliveryError && !deliveryRecord && !deliveryDenied) {
      return null
    }

    const selectedGroupLabel = selectedDeliveryGroupContext?.groupDisplayName ?? ''
    const savedGroupLabel = deliveryRecord?.deliveryContext.grantedGroupContext.groupDisplayName ?? selectedGroupLabel
    const receiverMembershipRole = deliveryRecord?.deliveryContext.receiverActorContext.membershipRole ?? actorContext?.membershipRole ?? ''
    const receiverIdentityLabel = resolveReceiverIdentityLabel(deliveryRecord, actorContext)
    const publishedByLabel = resolveStoredActorContextLabel(
      deliveryRecord?.version.publishedByContext,
      actorContext?.tenantMembershipId,
      receiverIdentityLabel
    )
    let deliveryStatus: LotStoryGroupDeliveryView['status'] = 'denied'

    if (deliveryLoading) {
      deliveryStatus = 'loading'
    } else if (deliveryError) {
      deliveryStatus = 'error'
    } else if (deliveryRecord) {
      deliveryStatus = 'allowed'
    }

    return {
      status: deliveryStatus,
      identityLabel: receiverIdentityLabel,
      roleLabel: receiverMembershipRole,
      selectedGroupLabel,
      savedGroupLabel,
      selectedGroupMatchesSavedIntent: Boolean(
        deliveryRecord?.deliveryContext.grantedGroupContext.groupId
        && selectedDeliveryGroupId
        && deliveryRecord.deliveryContext.grantedGroupContext.groupId === selectedDeliveryGroupId
      ),
      partnerOrganizationsLabel: selectedDeliveryGroupContext?.partnerOrganizationName ?? t('communityProfile.access.none'),
      grantedGroupLabel: deliveryRecord?.deliveryContext.grantedGroupContext.groupDisplayName ?? '',
      grantedGroupRoleLabel: deliveryRecord
        ? t(`ownerConsole.groups.role.${deliveryRecord.deliveryContext.grantedGroupContext.groupRole}` as const)
        : '',
      publishedAtLabel: formatOptionalTimestamp(deliveryRecord?.version.publishedAt),
      publishedByLabel,
      deliveryTitle: deliveryRecord?.version.title ?? '',
      deliverySummary: deliveryRecord?.version.summary?.trim() ?? '',
      deliveryNotes: deliveryRecord?.version.notes?.trim() ?? '',
      blockingReasonLabels: deliveryBlockingReasonLabels,
      projection: deliveryRecord?.version.projectionSnapshot ?? null,
      errorMessage: deliveryError || null
    }
  }, [actorContext, deliveryBlockingReasonLabels, deliveryDenied, deliveryError, deliveryLoading, deliveryRecord, selectedDeliveryArtifactId, selectedDeliveryGroupContext, selectedDeliveryGroupId, t])

  const selectedDeliveryProjectionSummary = useMemo(() => {
    const projection = deliveryRecord?.version.projectionSnapshot
    if (!projection) return null

    const familyLabel = getStoryFamilyLabel(projection)
    const familyTechnicalId = getStoryFamilyTechnicalId(projection)
    const readinessLabel = getLotStoryReadinessLabel(t, projection.readiness.status)

    return (
      <div style={{ display: 'grid', gap: 8 }}>
        <strong style={{ fontSize: 12, color: '#166534' }}>{t('communityProfile.groupDeliveries.snapshot.title')}</strong>
        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
          <div style={{ border: '1px solid #bbf7d0', borderRadius: 10, backgroundColor: '#ffffff', padding: 10, display: 'grid', gap: 4 }}>
            <strong style={{ fontSize: 12, color: '#166534' }}>{t('lots.story.summary.lineageRoot')}</strong>
            <div style={{ fontSize: 13, color: '#111827', wordBreak: 'break-word' }}>{familyLabel}</div>
            {familyTechnicalId ? (
              <div style={{ fontSize: 12, color: '#166534', wordBreak: 'break-word' }}>{familyTechnicalId}</div>
            ) : null}
          </div>
          <div style={{ border: '1px solid #bbf7d0', borderRadius: 10, backgroundColor: '#ffffff', padding: 10, display: 'grid', gap: 4 }}>
            <strong style={{ fontSize: 12, color: '#166534' }}>{t('lots.story.summary.currentSite')}</strong>
            <div style={{ fontSize: 13, color: '#111827' }}>{formatProjectionCurrentSite(deliveryRecord)}</div>
          </div>
          <div style={{ border: '1px solid #bbf7d0', borderRadius: 10, backgroundColor: '#ffffff', padding: 10, display: 'grid', gap: 4 }}>
            <strong style={{ fontSize: 12, color: '#166534' }}>{t('lots.story.summary.species')}</strong>
            <div style={{ fontSize: 13, color: '#111827' }}>{projection.identityContext.species?.trim() || '—'}</div>
          </div>
          <div style={{ border: '1px solid #bbf7d0', borderRadius: 10, backgroundColor: '#ffffff', padding: 10, display: 'grid', gap: 6 }}>
            <strong style={{ fontSize: 12, color: '#166534' }}>{t('communityProfile.groupDeliveries.snapshot.readiness')}</strong>
            <span style={{ ...getLotStoryReadinessBadgeStyle(projection.readiness.status), width: 'fit-content', padding: '4px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
              {readinessLabel}
            </span>
          </div>
        </div>
      </div>
    )
  }, [deliveryRecord, t])

  const selectedDeliveryContextIdentityLabel = useMemo(() => (
    actorContext?.communityDisplayName?.trim()
    || actorContext?.accountDisplayName?.trim()
    || actorContext?.actorId?.trim()
    || ''
  ), [actorContext])

  const selectedDeliveryContextTenantRoleLabel = useMemo(() => (
    actorContext?.membershipRole ?? ''
  ), [actorContext?.membershipRole])

  const selectedDeliveryContextGroupRoleLabel = useMemo(() => (
    selectedDeliveryGroupContext?.groupRole
      ? t(`ownerConsole.groups.role.${selectedDeliveryGroupContext.groupRole}` as const)
      : ''
  ), [selectedDeliveryGroupContext?.groupRole, t])

  const handleDeliveryGroupChange = (nextGroupId: string) => {
    setSelectedDeliveryGroupId(nextGroupId.trim())
    setSelectedDeliveryArtifactId('')
    deliveryDetailRequestIdRef.current += 1
    setDeliveryRecord(null)
    setDeliveryLoading(false)
    setDeliveryDenied(false)
    setDeliveryBlockingReasonLabels([])
    setDeliveryError('')
  }

  const openDeliverySnapshot = async (artifactId: string) => {
    const nextArtifactId = artifactId.trim()
    if (!nextArtifactId) return

    setSelectedDeliveryArtifactId(nextArtifactId)
    const nextRequestId = deliveryDetailRequestIdRef.current + 1
    deliveryDetailRequestIdRef.current = nextRequestId
    setDeliveryLoading(true)
    setDeliveryDenied(false)
    setDeliveryBlockingReasonLabels([])
    setDeliveryError('')
    setDeliveryRecord(null)

    try {
      const result = await api.lotStories.getGroupDelivery(nextArtifactId)
      if (deliveryDetailRequestIdRef.current !== nextRequestId) return
      setDeliveryRecord(result)
    } catch (loadError) {
      if (deliveryDetailRequestIdRef.current !== nextRequestId) return
      if (loadError instanceof ApiError && loadError.status === 403) {
        const blockingReasons = extractGroupDeliveryBlockingReasons(loadError)
        setDeliveryDenied(true)
        setDeliveryBlockingReasonLabels(
          blockingReasons.length > 0
            ? blockingReasons.map((reason) => getGroupDeliveryBlockingReasonLabel(reason, t))
            : [t('lots.story.groupDelivery.reason.groupReadNotAllowed')]
        )
        setDeliveryList((current) => {
          if (!current) {
            return current
          }

          if (shouldClearSelectedGroupDeliveries(blockingReasons)) {
            return {
              ...current,
              deliveries: []
            }
          }

          return {
            ...current,
            deliveries: current.deliveries.filter((entry) => entry.artifact.artifactId !== nextArtifactId)
          }
        })

        if (shouldRefreshDeliveryActorContext(blockingReasons)) {
          try {
            const refreshedActorContext = await api.communityActorContext.get()
            if (deliveryDetailRequestIdRef.current !== nextRequestId) return
            setActorContext(refreshedActorContext)
          } catch {
            // Keep the denied state visible even if the live actor-context refresh fails.
          }
        }

        setDeliveryError('')
      } else {
        setDeliveryError(loadError instanceof Error ? loadError.message : t('communityProfile.groupDeliveries.error.detailFailed'))
      }
    } finally {
      if (deliveryDetailRequestIdRef.current === nextRequestId) {
        setDeliveryLoading(false)
      }
    }
  }

  return (
    <div style={{ maxWidth: 860 }}>
      <h2 style={{ marginTop: 0 }}>{t('communityDeliveries.title')}</h2>
      <p style={{ color: '#6b7280', marginTop: 8 }}>{t('communityDeliveries.subtitle')}</p>

      {error ? (
        <div data-testid="community-deliveries-error" style={{ padding: 12, borderRadius: 8, marginTop: 12, backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b' }}>
          {error}
        </div>
      ) : null}

      <div data-testid="community-deliveries-section" style={{ marginTop: 18, padding: 16, borderRadius: 12, border: '1px solid #e5e7eb', backgroundColor: 'white' }}>
        <h3 style={{ marginTop: 0 }}>{t('communityProfile.groupDeliveries.title')}</h3>
        <p style={{ color: '#6b7280', marginTop: 8 }}>{t('communityProfile.groupDeliveries.subtitle')}</p>

        {isLoading ? (
          <div style={{ color: '#6b7280' }}>{t('communityProfile.loading')}</div>
        ) : activeGroupContexts.length === 0 ? (
          <div data-testid="community-deliveries-empty" style={{ color: '#6b7280', marginTop: 12 }}>
            {t('communityProfile.groupDeliveries.noActiveGroups')}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ fontWeight: 600 }}>{t('communityProfile.groupDeliveries.groupSelect')}</span>
              <select
                data-testid="community-deliveries-group-select"
                value={selectedDeliveryGroupId}
                onChange={(event) => handleDeliveryGroupChange(event.target.value)}
                style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
              >
                {activeGroupContexts.map((groupContext) => (
                  <option key={groupContext.groupId} value={groupContext.groupId}>
                    {groupContext.groupDisplayName}
                  </option>
                ))}
              </select>
            </label>

            <div
              data-testid="community-deliveries-context"
              style={{
                display: 'grid',
                gap: 8,
                padding: 12,
                borderRadius: 10,
                border: '1px solid #d1fae5',
                backgroundColor: '#f0fdf4'
              }}
            >
              <strong style={{ fontSize: 12, color: '#166534' }}>{t('communityProfile.groupDeliveries.context.title')}</strong>
              <div style={{ fontSize: 13, color: '#166534' }}>{t('communityProfile.groupDeliveries.context.subtitle')}</div>
              <div style={{ display: 'grid', gap: 6, fontSize: 13, color: '#166534' }}>
                <div data-testid="community-deliveries-context-identity">
                  <strong>{t('lots.story.groupDelivery.identity')}:</strong> {selectedDeliveryContextIdentityLabel || t('communityProfile.access.none')}
                </div>
                <div data-testid="community-deliveries-context-tenant-role">
                  <strong>{t('lots.story.groupDelivery.tenantRole')}:</strong> {selectedDeliveryContextTenantRoleLabel || t('communityProfile.access.none')}
                </div>
                <div data-testid="community-deliveries-context-selected-group">
                  <strong>{t('communityProfile.groupDeliveries.context.selectedGroup')}:</strong> {selectedDeliveryGroupContext?.groupDisplayName || t('communityProfile.access.none')}
                </div>
                <div data-testid="community-deliveries-context-selected-group-role">
                  <strong>{t('communityProfile.groupDeliveries.context.selectedGroupRole')}:</strong> {selectedDeliveryContextGroupRoleLabel || t('communityProfile.access.none')}
                </div>
                <div data-testid="community-deliveries-context-partners">
                  <strong>{t('lots.story.groupDelivery.partnerOrganizations')}:</strong> {selectedDeliveryGroupContext?.partnerOrganizationName || t('communityProfile.access.none')}
                </div>
              </div>
            </div>

            {deliveryListLoading ? (
              <div data-testid="community-deliveries-loading" style={{ color: '#6b7280' }}>
                {t('communityProfile.groupDeliveries.loading')}
              </div>
            ) : deliveryListError ? (
              <div data-testid="community-deliveries-list-error" style={{ padding: 12, borderRadius: 8, backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b' }}>
                {deliveryListError}
              </div>
            ) : deliveryList && deliveryList.deliveries.length === 0 ? (
              <div data-testid="community-deliveries-empty" style={{ color: '#6b7280' }}>
                {t('communityProfile.groupDeliveries.empty')}
              </div>
            ) : deliveryList ? (
              <>
                <div data-testid="community-deliveries-count" style={{ fontSize: 12, fontWeight: 700, color: '#166534' }}>
                  {t('communityProfile.groupDeliveries.count', { count: deliveryList.deliveries.length })}
                </div>
                <div style={{ display: 'grid', gap: 12 }}>
                  {deliveryList.deliveries.map((entry) => {
                    const isSelected = entry.artifact.artifactId === selectedDeliveryArtifactId
                    const selectedGroupLabel = selectedDeliveryGroupContext?.groupDisplayName ?? ''
                    const selectedGroupRoleLabel = formatGroupRoleLabel(selectedDeliveryGroupContext?.groupRole, t)
                    const grantedGroupRoleLabel = formatGroupRoleLabel(entry.deliveryContext.grantedGroupContext.groupRole, t)
                    const publishedByLabel = resolveStoredActorContextLabel(
                      entry.version.publishedByContext,
                      actorContext?.tenantMembershipId,
                      selectedDeliveryContextIdentityLabel
                    )
                    const selectedGroupMatchesDelivery = Boolean(
                      selectedDeliveryGroupContext?.groupId
                      && selectedDeliveryGroupContext.groupId === entry.deliveryContext.grantedGroupContext.groupId
                    )
                    const selectedPartnerLabel = selectedDeliveryGroupContext?.partnerOrganizationName ?? t('communityProfile.access.none')
                    return (
                      <div
                        key={entry.artifact.artifactId}
                        data-testid={`community-deliveries-item-${entry.artifact.artifactId}`}
                        style={{
                          border: isSelected ? '1px solid #86efac' : '1px solid #e5e7eb',
                          borderRadius: 12,
                          padding: 12,
                          backgroundColor: isSelected ? '#f0fdf4' : '#fcfcfd',
                          display: 'grid',
                          gap: 8
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                          <div style={{ display: 'grid', gap: 4 }}>
                            <strong style={{ fontSize: 14, color: '#111827' }}>{entry.version.title}</strong>
                            <div data-testid={`community-deliveries-item-${entry.artifact.artifactId}-granted-group`} style={{ fontSize: 12, color: '#475569' }}>
                              <strong>{t('communityProfile.groupDeliveries.list.grantedGroup')}:</strong> {entry.deliveryContext.grantedGroupContext.groupDisplayName}
                            </div>
                          </div>
                          <span style={{ ...getLotStoryReadinessBadgeStyle(entry.version.readinessStatus), padding: '4px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                            {getLotStoryReadinessLabel(t, entry.version.readinessStatus)}
                          </span>
                        </div>
                        <div style={{ display: 'grid', gap: 4, fontSize: 12, color: '#166534' }}>
                          <div data-testid={`community-deliveries-item-${entry.artifact.artifactId}-selected-group`}>
                            <strong>{t('communityProfile.groupDeliveries.list.selectedGroup')}:</strong> {selectedGroupLabel || t('communityProfile.access.none')}
                          </div>
                          <div data-testid={`community-deliveries-item-${entry.artifact.artifactId}-selected-group-role`}>
                            <strong>{t('communityProfile.groupDeliveries.list.selectedGroupRole')}:</strong> {selectedGroupRoleLabel || t('communityProfile.access.none')}
                          </div>
                          <div data-testid={`community-deliveries-item-${entry.artifact.artifactId}-granted-group-role`}>
                            <strong>{t('communityProfile.groupDeliveries.list.grantedGroupRole')}:</strong> {grantedGroupRoleLabel || t('communityProfile.access.none')}
                          </div>
                          <div data-testid={`community-deliveries-item-${entry.artifact.artifactId}-partner`}>
                            <strong>{t('communityProfile.groupDeliveries.list.partner')}:</strong> {selectedPartnerLabel}
                          </div>
                          <div data-testid={`community-deliveries-item-${entry.artifact.artifactId}-published-by`}>
                            <strong>{t('communityProfile.groupDeliveries.list.publishedBy')}:</strong> {publishedByLabel || t('communityProfile.access.none')}
                          </div>
                          <div data-testid={`community-deliveries-item-${entry.artifact.artifactId}-selected-match`}>
                            <strong>{t('communityProfile.groupDeliveries.list.selectedMatch')}:</strong> {selectedGroupMatchesDelivery
                              ? t('lots.story.groupShareReview.savedMatch.yes')
                              : t('lots.story.groupShareReview.savedMatch.no')}
                          </div>
                        </div>
                        {entry.version.summary ? (
                          <div style={{ fontSize: 13, color: '#374151' }}>{entry.version.summary}</div>
                        ) : null}
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {t('communityProfile.groupDeliveries.publishedAt')}: {formatOptionalTimestamp(entry.version.publishedAt) || '—'}
                          </div>
                          <button
                            type="button"
                            data-testid={`community-deliveries-open-${entry.artifact.artifactId}`}
                            onClick={() => void openDeliverySnapshot(entry.artifact.artifactId)}
                            disabled={deliveryLoading && isSelected}
                            style={{
                              padding: '8px 12px',
                              borderRadius: 8,
                              border: '1px solid #166534',
                              backgroundColor: '#ffffff',
                              color: '#166534',
                              fontWeight: 700,
                              cursor: deliveryLoading && isSelected ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {deliveryLoading && isSelected
                              ? t('communityProfile.groupDeliveries.opening')
                              : isSelected
                                ? t('communityProfile.groupDeliveries.opened')
                                : t('communityProfile.groupDeliveries.open')}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </>
            ) : null}

          </div>
        )}

        {selectedDeliveryView ? (
          <div style={{ marginTop: 12 }}>
            <LotStoryGroupDeliveryCard
              t={t}
              delivery={selectedDeliveryView}
              testIdPrefix="community-deliveries-group-delivery"
              renderedProjection={selectedDeliveryProjectionSummary}
            />
          </div>
        ) : activeGroupContexts.length > 0 && deliveryList && deliveryList.deliveries.length > 0 ? (
          <div data-testid="community-deliveries-placeholder" style={{ color: '#6b7280', fontSize: 13, marginTop: 12 }}>
            {t('communityProfile.groupDeliveries.placeholder')}
          </div>
        ) : null}
      </div>
    </div>
  )
}